import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface PortfolioRow {
  readonly portfolioId: string;
  readonly portfolioCode: string;
  readonly portfolioName: string;
  readonly portfolioType: string;
  readonly budgetAllocated: string;
  readonly budgetConsumed: string;
  readonly status: string;
}

const TYPES = ['', 'STRATEGIC', 'OPERATIONAL', 'COMPLIANCE', 'INNOVATION', 'TECHNOLOGY', 'TRANSFORMATION'];
const STATUSES = ['', 'PROPOSED', 'ACTIVE', 'ON_HOLD', 'UNDER_REVIEW', 'CLOSED'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M33 — Portfolio Dashboard (§31 / §17 / §18). Register of investment
 * portfolios (T443) with portfolio class, allocated vs consumed budget and the
 * propose→activate→review→hold→close lifecycle. Supports authoring a portfolio
 * and driving it — activate, approve, review, allocate-budget, record-benefit,
 * hold, resume and close — each wired to the guarded /v1/pm/portfolios write
 * API. Filterable by portfolio type and status.
 */
export function PortfolioDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<PortfolioRow[]>([]);
  const [portfolioType, setPortfolioType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState('STRATEGIC');
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (portfolioType) params.set('portfolioType', portfolioType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: PortfolioRow[] }>(`/v1/pm/portfolios${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, portfolioType, status, reload]);

  const failMessage = (err: unknown, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m33.common.notAuthorised', { perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newName.trim()) { setActionError(t('m33.portfolio.validation')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/pm/portfolios`, {
        method: 'POST',
        body: { portfolioName: newName.trim(), portfolioType: newType },
        token,
      });
      setNotice(t('m33.portfolio.created', { name: newName.trim() }));
      setNewName('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, 'PORTFOLIO_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: PortfolioRow, path: string, label: string, perm: string, body?: Record<string, unknown>): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/pm/portfolios/${r.portfolioId}/${path}`, { method: 'POST', body: body ?? {}, token });
      setNotice(t('m33.common.notice', { code: r.portfolioCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, perm));
    }
  };

  return (
    <main aria-label="portfolio-dashboard">
      <h1>{t('m33.portfolio.title')}</h1>

      <section aria-label="create-portfolio">
        <h2>{t('m33.portfolio.createHeading')}</h2>
        <form aria-label="portfolio-form" onSubmit={(e) => void create(e)}>
          <label>{t('m33.common.name')}<input aria-label="field-portfolio-name" value={newName} onChange={(e) => setNewName(e.target.value)} /></label>
          <label>{t('m33.common.type')}
            <select aria-label="field-portfolio-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-portfolio" disabled={busy}>{busy ? t('m33.portfolio.creating') : t('m33.portfolio.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="portfolio-error">{actionError}</p>}
        {notice && <p aria-label="portfolio-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m33.common.type')}
          <select aria-label="filter-portfolio-type" value={portfolioType} onChange={(e) => setPortfolioType(e.target.value)}>
            {TYPES.map((ty) => <option key={ty || 'all'} value={ty}>{ty || t('m33.common.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m33.common.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m33.common.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m33.common.loading')}</p> : (
        <>
          <p aria-label="portfolio-count">{t('m33.portfolio.count', { n: rows.length })}</p>
          <DataTable
            ariaLabel="portfolio-table"
            rows={rows}
            rowKey={(r) => r.portfolioId}
            columns={[
              { key: 'portfolioCode', header: t('m33.common.code'), render: (r) => r.portfolioCode },
              { key: 'portfolioName', header: t('m33.common.name'), render: (r) => r.portfolioName },
              { key: 'portfolioType', header: t('m33.common.type'), render: (r) => r.portfolioType },
              { key: 'budgetAllocated', header: t('m33.portfolio.colAllocated'), render: (r) => r.budgetAllocated },
              { key: 'budgetConsumed', header: t('m33.portfolio.colConsumed'), render: (r) => r.budgetConsumed },
              { key: 'status', header: t('m33.common.status'), render: (r) => r.status },
              { key: 'actions', header: t('m33.common.actions'), render: (r) => (
                <>
                  {r.status === 'PROPOSED' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`approve-${r.portfolioCode}`} onClick={() => void act(r, 'approve', t('m33.portfolio.act.approved'), 'PORTFOLIO_APPROVE')}>{t('m33.portfolio.btnApprove')}</Button>
                      <Button variant="secondary" type="button" aria-label={`activate-${r.portfolioCode}`} onClick={() => void act(r, 'activate', t('m33.portfolio.act.activated'), 'PORTFOLIO_APPROVE')}>{t('m33.portfolio.btnActivate')}</Button>
                    </>
                  )}
                  {r.status === 'ACTIVE' && (
                    <>
                      <Button variant="secondary" type="button" aria-label={`review-${r.portfolioCode}`} onClick={() => void act(r, 'review', t('m33.portfolio.act.reviewed'), 'PORTFOLIO_REVIEW')}>{t('m33.portfolio.btnReview')}</Button>
                      <Button variant="secondary" type="button" aria-label={`hold-${r.portfolioCode}`} onClick={() => void act(r, 'hold', t('m33.portfolio.act.onHold'), 'PORTFOLIO_MANAGE')}>{t('m33.portfolio.btnHold')}</Button>
                      <input aria-label={`amount-${r.portfolioCode}`} type="number" value={amounts[r.portfolioId] ?? ''} onChange={(e) => setAmounts((m) => ({ ...m, [r.portfolioId]: e.target.value }))} placeholder={t('m33.portfolio.amountPh')} />
                      <Button variant="secondary" type="button" aria-label={`allocate-budget-${r.portfolioCode}`} onClick={() => void act(r, 'allocate-budget', t('m33.portfolio.act.budgetAllocated'), 'PORTFOLIO_MANAGE', { amount: Number(amounts[r.portfolioId] || 0) })}>{t('m33.portfolio.btnAllocate')}</Button>
                      <Button variant="primary" type="button" aria-label={`record-benefit-${r.portfolioCode}`} onClick={() => void act(r, 'record-benefit', t('m33.portfolio.act.benefitRecorded'), 'PORTFOLIO_MANAGE', { amount: Number(amounts[r.portfolioId] || 0) })}>{t('m33.portfolio.btnRecordBenefit')}</Button>
                    </>
                  )}
                  {r.status === 'ON_HOLD' && (
                    <Button variant="secondary" type="button" aria-label={`resume-${r.portfolioCode}`} onClick={() => void act(r, 'resume', t('m33.portfolio.act.resumed'), 'PORTFOLIO_MANAGE')}>{t('m33.portfolio.btnResume')}</Button>
                  )}
                  {!['CLOSED'].includes(r.status) && (
                    <Button variant="secondary" type="button" aria-label={`close-${r.portfolioCode}`} onClick={() => void act(r, 'close', t('m33.portfolio.act.closed'), 'PORTFOLIO_MANAGE')}>{t('m33.portfolio.btnClose')}</Button>
                  )}
                </>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
